import React, { useEffect, useMemo, useState } from "react";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Register core plugins (NOT datalabels globally)
ChartJS.register(ArcElement, Tooltip, Legend);

// --- Styled (card-friendly) ---
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px 15px;
  box-sizing: border-box;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 6px;
`;

const ChartArea = styled.div`
  flex: 1;
  width: 100%;
  min-height: 260px;   /* keeps it visible in smaller cards */
  position: relative;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

// Colors (rule: TP = green, Others = orange)
const GREEN = "#2e7d32";
const GREEN_BORDER = "#2e7d32";
const ORANGE = "#fb8c00";
const ORANGE_BORDER = "#fb8c00";

const monthNames = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
];

const TpTransactionsPie = ({ salesData = [] }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");

  useEffect(() => {
    if (!salesData.length) return;
    const years = [...new Set(salesData.map(r => r.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear(years.includes("2024") ? "2024" : years[years.length - 1]);
  }, [salesData]);

  const { tpCount, otherCount, chartData } = useMemo(() => {
    if (!selectedYear) return { tpCount: 0, otherCount: 0, chartData: null };

    const filtered = salesData.filter(row =>
      row.Rok.toString() === selectedYear &&
      (selectedMonth === "all" || parseInt(row.Miesiac, 10) === parseInt(selectedMonth, 10))
    );

    let tp = 0;
    let other = 0;

    filtered.forEach(row => {
      // Count transactions (rows). TP flagged with TP === "1"
      if (row.TP === "1") tp += 1;
      else other += 1;
    });

    const data = {
      labels: ["TP (spółki powiązane)", "Pozostałe"],
      datasets: [{
        data: [tp, other],
        backgroundColor: [GREEN, ORANGE],
        borderColor: [GREEN_BORDER, ORANGE_BORDER],
        borderWidth: 1,
      }],
    };

    return { tpCount: tp, otherCount: other, chartData: data };
  }, [salesData, selectedYear, selectedMonth]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Transakcje ze spółkami powiązanymi
        <InfoIconWrapper
          data-tooltip-id="tp-pie-tooltip"
          data-tooltip-content={
            "Porównanie liczby transakcji\ntypu TP (spółki powiązane) vs pozostałe\nw wybranym okresie."
          }
          data-tooltip-place="left"
        >
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>

      <InfoTooltip
        id="tp-pie-tooltip"
        place="left"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "white" }}
      />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Miesiąc:</label>
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="all">Cały rok</option>
            {monthNames.map((name, idx) => (
              <option key={idx} value={idx + 1}>{name}</option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      {chartData ? (
        <ChartArea>
          <Pie
            data={chartData}
            plugins={[ChartDataLabels]}
            options={{
              responsive: true,
              maintainAspectRatio: false, // fill ChartArea
              plugins: {
                legend: { position: "bottom" },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      const total = tpCount + otherCount || 1;
                      const v = ctx.raw ?? 0;
                      const pct = ((v / total) * 100).toFixed(1);
                      return `${ctx.label}: ${v.toLocaleString("pl-PL")} (${pct}%)`;
                    }
                  }
                },
                datalabels: {
                  color: "#fff",
                  font: { size: 11, weight: "bold" },
                  formatter: (value, ctx) => {
                    const arr = ctx.dataset.data || [];
                    const total = arr.reduce((a, b) => a + b, 0) || 1;
                    return `${((value / total) * 100).toFixed(1)}%`;
                  },
                },
              },
            }}
          />
        </ChartArea>
      ) : (
        <p style={{ textAlign: "center", marginTop: 8 }}>Brak danych do wyświetlenia.</p>
      )}
    </ChartWrapper>
  );
};

export default TpTransactionsPie;
